import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { FolderOpen, Utensils } from "lucide-react";

import { categoriesGetAll, itemsGetAll } from "~/api-refs";

interface LinkedItemsPreviewProps {
  routingMode: "categories" | "items";
  categoriesSelected: string[];
  itemsSelected: string[];
}

export function LinkedItemsPreview({
  routingMode,
  categoriesSelected,
  itemsSelected,
}: LinkedItemsPreviewProps) {
  const { data: categoriesResult } = useQuery({
    ...convexQuery(categoriesGetAll, {
      includeItemsCount: true,
      paginationOpts: { numItems: 1000, cursor: null },
    }),
    enabled: routingMode === "categories" && categoriesSelected.length > 0,
  });

  const { data: itemsResult } = useQuery({
    ...convexQuery(itemsGetAll, {
      paginationOpts: { numItems: 1000, cursor: null },
    }),
    enabled: routingMode === "items" && itemsSelected.length > 0,
  });

  const categories =
    categoriesResult?.page?.filter((c) => categoriesSelected.includes(c._id)) ??
    [];
  const items =
    itemsResult?.page?.filter((i) => itemsSelected.includes(i._id)) ?? [];

  if (
    (routingMode === "categories" && categories.length === 0) ||
    (routingMode === "items" && items.length === 0)
  ) {
    return null;
  }

  return (
    <div className="w-80">
      <h3 className="subtitle-sm mb-3">
        Linked {routingMode === "categories" ? "Categories" : "Items"}
      </h3>
      <div className="border-border max-h-96 space-y-2 overflow-y-auto rounded-xl border p-3">
        {routingMode === "categories"
          ? categories.map((cat) => (
              <div
                className="bg-accent flex items-center gap-2 rounded-lg px-3 py-2"
                key={cat._id}
              >
                <FolderOpen className="text-category h-4 w-4" />
                <span className="flex-1">{cat.name}</span>
                <span className="text-muted-foreground text-xs">
                  {cat.itemCount} items
                </span>
              </div>
            ))
          : items.map((item) => (
              <div
                className="bg-accent flex items-center gap-2 rounded-lg px-3 py-2"
                key={item._id}
              >
                <Utensils className="text-item h-4 w-4" />
                <span>{item.name}</span>
              </div>
            ))}
      </div>
    </div>
  );
}
